import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useAuth } from "@/hooks/useAuth";
import { lenderMatch } from "@/lib/lenderMatch.functions";
import { requestLenderIntro } from "@/lib/lenderIntro.functions";
import type { LenderMatchResult } from "@/lib/lenderMatch.functions";
type ShortlistEntry = LenderMatchResult["lenders"][number];
import { trackEvent } from "@/lib/analytics";
import { dgTrack } from "@/lib/dataGraph";

/**
 * One-tap lender handoff on Stack results: shows the best-fit partner lender
 * and lets a signed-in member ask them to reach out. National (non-partner)
 * options never take intro requests, so they are skipped here.
 */
export default function TopLenderHandoff({
  state,
  programs,
  fallbackHref,
}: {
  state: string | null;
  programs: string[];
  fallbackHref: string;
}) {
  const { user } = useAuth();
  const [lender, setLender] = useState<ShortlistEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  useEffect(() => {
    let cancelled = false;
    lenderMatch({ data: { state, selected_grants: programs.slice(0, 10) } as any })
      .then((res) => {
        if (cancelled) return;
        const top = res.lenders.find((l) => !l.is_national) ?? null;
        setLender(top);
        if (top) trackEvent("lender_shortlist_impression", { lender: top.name, source: "stack-handoff" });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, programs.join("|")]);

  if (loading) {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Finding your best-fit lender…
      </div>
    );
  }
  if (!lender) {
    return (
      <div className="mt-4">
        <Button asChild size="sm"><Link to={fallbackHref}>Match my Stack</Link></Button>
      </div>
    );
  }

  const ask = async () => {
    setStatus("sending");
    try {
      await requestLenderIntro({ data: { lenderId: lender.id, selectedGrants: programs.slice(0, 10), notes: "stack-handoff" } });
      setStatus("sent");
      trackEvent("lender_intro_requested", { lender: lender.name, source: "stack-handoff" });
      dgTrack("compliance", "lender_intro_requested", { source: "stack-handoff", lender_id: lender.id, lender_name: lender.name });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-border bg-background p-4">
      <p className="text-xs font-semibold uppercase text-muted-foreground">Best fit for your Stack</p>
      <p className="mt-1 font-semibold text-foreground">
        {lender.name}
        {lender.contact_name ? <span className="font-normal text-muted-foreground"> · {lender.contact_name}</span> : null}
      </p>
      {lender.reasons?.[0] && <p className="mt-0.5 text-sm text-muted-foreground">{lender.reasons[0]}</p>}
      <div className="mt-3">
        {status === "sent" ? (
          <p className="flex items-center gap-1.5 text-sm font-medium text-primary">
            <CheckCircle2 className="h-4 w-4" /> Request sent. {lender.contact_name || lender.name} will reach out soon.
          </p>
        ) : user ? (
          <Button onClick={ask} disabled={status === "sending"} className="bg-accent text-accent-foreground hover:bg-accent/90">
            {status === "sending" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Have them reach out
          </Button>
        ) : (
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to="/auth?returnTo=/grant-finder">Sign in to have them reach out</Link>
          </Button>
        )}
        {status === "error" && <p className="mt-2 text-sm text-destructive">That didn't go through. Please try again.</p>}
      </div>
      <p className="mt-3 text-sm">
        <Link to={fallbackHref} className="text-primary underline-offset-2 hover:underline">Compare other lenders instead</Link>
      </p>
      <p className="mt-3 flex gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        No credit pull, no obligation. No one pays to be here or to rank higher; Home Approach is paid only by
        employers. You're never required to use a matched lender.
      </p>
    </div>
  );
}
